import React from 'react';
import { View, ScrollView, StyleSheet, Platform, Text, Image, TouchableOpacity } from 'react-native';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMyBarTimeTableStore } from 'src/domain/stores/useMyBarTimeTableStore';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import { formatTimetableRange } from 'src/core/utils/formatTimetableTime';
import type Constants from 'src/core/utils/Constants';

export default function TimetableLayoutScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenTimeTablePage>>();
	const EventStore = useEventStore();
	const MyBarTimeTableStore = useMyBarTimeTableStore();

	const data = route?.params?.data;
	const title = route?.params?.title;
	// Opened from the registration form the screen is read-only: content, no heart.
	const showFavorite = route?.params?.showFavorite ?? true;

	// Straight from the store, which owns how a session is identified: rebuilding that key here left the heart
	// empty on a session it had just saved.
	const favorite = useMyBarTimeTableStore((state) => (data ? state.isFavorite(data) : false));

	const toggleFavorite = () => {
		if (!data) return;
		MyBarTimeTableStore.toggleFavorite(data, data.eventUuid);
	};

	if (!data) return null;

	const timeRange = formatTimetableRange(data);

	return (
		<View style={styles.layoutContainer}>
			<NavBar
				backBtn={true}
				bgImage={EventStore.activeTheme.header_bg_image}
				bgcolor={EventStore.activeTheme.header_bg_color}
				color={EventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={EventStore.activeTheme.statusbar_color}
				title={Platform.OS === 'ios' ? title : data.navTitle}
			/>
			{Platform.OS === 'ios' ? (
				<ScrollView>
					<View style={styles.container}>
						{data.image ? <Image resizeMode="cover" source={{ uri: data.image }} style={styles.img} /> : null}
						<Text
							style={[
								styles.title,
								{
									color: EventStore.activeTheme.heading_text_color,
									display: 'flex',
									flex: showFavorite ? 0.8 : 1,
									alignSelf: 'center',
								},
							]}
						>
							{data.title}
						</Text>
						{showFavorite ? (
							<TouchableOpacity
								accessibilityRole="button"
								accessibilityState={{ selected: favorite }}
								style={{ width: 54, height: 54, display: 'flex', flex: 0.2, alignItems: 'center', justifyContent: 'center' }}
								testID="favorite-toggle"
								onPress={toggleFavorite}
							>
								{Platform.OS === 'ios' ? (
									<Ionicons name={favorite ? 'heart' : 'heart-outline'} size={26} style={{ color: Colors.red }} />
								) : (
									<MaterialIcons
										name="favorite"
										size={24}
										style={favorite ? { color: Colors.red } : { color: Colors.overlay12 }}
									/>
								)}
							</TouchableOpacity>
						) : null}
						<Text style={styles.timeStamps}>{timeRange}</Text>
						<Text style={styles.stageName}>{data.stageTitle}</Text>
						<View style={styles.underLine} />
						<Text style={[styles.title, { color: EventStore.activeTheme.heading_text_color }]}>
							{Translations.textExtraInformation}
						</Text>
						<Text style={styles.content}>{data.description}</Text>
					</View>
				</ScrollView>
			) : (
				<ScrollView>
					<View style={styles.container}>
						{data.image ? <Image resizeMode="cover" source={{ uri: data.image }} style={styles.img} /> : null}
						<Text
							style={[
								styles.title,
								{
									color: EventStore.activeTheme.heading_text_color,
									display: 'flex',
									flex: showFavorite ? 0.8 : 1,
									alignSelf: 'center',
								},
							]}
						>
							{data.title}
						</Text>
						{showFavorite ? (
							<TouchableOpacity
								accessibilityRole="button"
								accessibilityState={{ selected: favorite }}
								style={{ width: 54, height: 54, display: 'flex', flex: 0.2, alignItems: 'center', justifyContent: 'center' }}
								testID="favorite-toggle"
								onPress={toggleFavorite}
							>
								<MaterialIcons
									name="favorite"
									size={24}
									style={favorite ? { color: Colors.red } : { color: Colors.overlay12 }}
								/>
							</TouchableOpacity>
						) : null}
						<View style={styles.underLine} />
						<Text style={styles.timeStamps}>{timeRange}</Text>
						<Text style={styles.stageName}>{data.stageTitle}</Text>
						<Text style={styles.content}>{data.description}</Text>
					</View>
				</ScrollView>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		backgroundColor: Colors.white,
		paddingBottom: Dimens.padding30,
	},
	layoutContainer: {
		flex: 1,
	},
	img: {
		height: 210,
		width: '100%',
		marginBottom: Dimens.margin0,
	},
	title: {
		fontSize: Platform.OS === 'ios' ? 20 : 17,
		fontFamily: Fonts.montserratMedium,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		marginBottom: Platform.OS === 'ios' ? 12 : 0,
		width: '100%',
	},
	underLine: {
		backgroundColor: Platform.OS === 'ios' ? Colors.greyBg : Colors.overlay12,
		width: '100%',
		height: Platform.OS === 'ios' ? 4 : 1,
		marginTop: Platform.OS === 'ios' ? 0 : 0,
		marginBottom: Platform.OS === 'ios' ? 25 : 12,
	},
	timeStamps: {
		fontSize: Dimens.fontSize17,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratBold,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		width: '100%',
	},
	stageName: {
		fontSize: Dimens.fontSize17,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratLight,
		marginBottom: Dimens.margin20,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		width: '100%',
	},
	content: {
		fontSize: Dimens.fontSize17,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		lineHeight: Dimens.lineHeight23,
	},
});
